import { useRef } from 'react';

/**
 * Return a ref which is always updated with the current value.
 *
 * Equivalent to:
 *
 * ```tsx
 * const box = useRef<T>(current);
 * box.current = current;
 * ```
 *
 * This is useful because it allows effects and callbacks to close over the box,
 * rather than the current value:
 *
 * ```tsx
 * function MyButton(props: { onClick: () => void }) {
 *   const box = useRefBox({ props });
 *
 *   // callback closes over the ref, so we never have to re-render
 *   const clicked = useCallback(() => box.current.props.onClick(), [box]);
 *   return <button onClick={clicked}>Click me</button>;
 * }
 * ```
 *
 * This is particularly useful for avoiding unwanted effect teardowns.
 *
 * @param current - the current value to store in the ref
 * @returns a ref object that is updated with the current value
 */
export function useRefBox<T>(current: T): React.RefObject<T> {
  const ref = useRef<T>(current);
  ref.current = current;
  return ref;
}
